import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import Tippy from '@tippyjs/react';

import { actTypeOptions } from '../constants/offerConstants';
import { useV3OfferContext } from '../contexts/V3OfferContext';
import useV3OfferTemplate, { ORIGIN } from '../hooks/useV3OfferTemplate';
import V3OfferOriginCard from './V3OfferOriginCard';
import TicketsToBreakevenTooltip from './TicketsToBreakevenTooltip';
import { hasBreakevenColumn, breakevenCountLabel } from '../utils/breakevenDisplay';
import { isPrimaryOffer } from '../utils/lineupOrdering';
import '../styles/lineup.css';

const OR = 'OR';
const AND = 'AND';

const formatRoleOption = (option) => (
  <span className="deal-terms-form__role-option">
    <span className="deal-terms-form__role-dot" aria-hidden="true" />
    {option.label}
  </span>
);

const V3OfferDealTermsForm = ({ offer, actType, onChangeField, onChangeActType, poolMode, calculated }) => {
  const operator = offer.dollar_or_percentage_operator || OR;
  const selectedRole = actTypeOptions.find(o => o.value === actType) || null;

  // Pull team + ftux opener from context. The form lives inside V3OfferProvider
  // when mounted via V3FinancePage; tests render it without a provider so
  // `useV3OfferContext()` is undefined and we fall back to safe defaults.
  const ctx = useV3OfferContext();
  const team = ctx?.team;
  // Currency suffix follows the same source the rest of the offer UI uses
  // (team.base_currency_symbol). Defaults to USD when not set.
  const currencyLabel = team?.base_currency_symbol || 'USD';
  const templateLinked = !!offer?.template_id;
  const templateName = offer?.template?.name;
  // Eyebrow label mirrors the template's origin:
  //   blueprint        → "BLUEPRINT"
  //   saved template   → "TEMPLATE"
  //   nothing linked   → "CUSTOM"   (offer built from scratch)
  const templateEyebrow = !templateLinked
    ? 'CUSTOM'
    : (offer?.template?.is_blueprint ? 'BLUEPRINT' : 'TEMPLATE');

  // Tolerates a missing V3OfferProvider (test rendering) — the hook reports
  // `modified: false` and inert handlers in that case, so the inline meta
  // links + MODIFIED eyebrow tag below skip rendering automatically.
  const templateApi = useV3OfferTemplate();
  const isModified = !!templateApi.modified;
  // Blueprints are read-only (ability.rb only grants :read on Blueprint), so
  // hide the inline "Save to template" link when modified from a blueprint.
  // "Save as new" stays available for either origin.
  const canSaveToCurrent = templateApi.origin === ORIGIN.SAVED_TEMPLATE;

  // Tickets to breakeven — a result of the deal rather than an input, so it sits
  // at the right edge of this row (O-4774). The engine already derives it; the
  // simple editor surfaces it in its rail card and this is the advanced editor's
  // equivalent. Rendered in Pool mode too, where the guarantee inputs are hidden
  // but the breakeven still describes the show.
  //
  // Passed in as a prop, NOT read from the offer context: the context exposes a
  // `calculated` key but nothing ever dispatches SET_CALCULATED, so it is
  // permanently {}. The real figures are computed in useV3OfferBudget and handed
  // down from the page.
  const breakevenOffered = calculated?.artistEarnings?.ticketsToBreakevenDetail;
  const breakevenForecast = calculated?.artistEarningsForecast?.ticketsToBreakevenDetail;
  const showBreakeven = hasBreakevenColumn(breakevenOffered);
  const showBreakevenForecast = hasBreakevenColumn(breakevenForecast);

  return (
    <section className="deal-terms-form">
      {/* The readout below is a sibling, not a child: only this column reflows,
        * so narrowing the window never pushes the readout down. */}
      <div className="deal-terms-form__fields">
        {/* Not a <label>: this field has no associated input, and a <label>
          * wrapping multiple buttons synthesizes clicks on the first labelable
          * descendant when its non-interactive children are clicked — which
          * caused the gear popup to re-toggle on outside clicks. */}
        {/* O-5024: the primary offer's template is driven by the event template
          * control at the top of the page, which applies its offer template to
          * exactly this offer. Two controls over one value would contradict each
          * other, so supports keep theirs and the headliner does not. */}
        {!isPrimaryOffer(offer) && (
        <div className="deal-terms-form__label deal-terms-form__field--template">
          <span className="deal-terms-form__label-text">Offer template</span>
          <div
            className={`deal-terms-form__template ${templateLinked ? 'deal-terms-form__template--linked' : ''}`}
            data-testid="deal-terms-form-template-card"
          >
            <div
              className="deal-terms-form__template-body"
              data-testid="deal-terms-form-template-btn"
            >
              <span className="deal-terms-form__template-eyebrow">
                <i className="fal fa-th-large" aria-hidden="true" />
                <span>{templateEyebrow}</span>
                {isModified && (
                  <>
                    <span className="deal-terms-form__template-eyebrow-sep" aria-hidden="true">·</span>
                    <span
                      className="deal-terms-form__template-modified"
                      data-testid="deal-terms-form-template-modified"
                    >
                      MODIFIED
                    </span>
                  </>
                )}
              </span>
              <span className="deal-terms-form__template-name">
                {templateLinked && templateName ? templateName : 'Apply template…'}
              </span>
              {isModified && (
                <div
                  className="deal-terms-form__template-meta"
                  data-testid="deal-terms-form-template-meta"
                >
                  {canSaveToCurrent && (
                    <>
                      <button
                        type="button"
                        className="deal-terms-form__template-link"
                        onClick={templateApi.handlers.handleUpdate}
                        data-testid="deal-terms-form-template-save-current"
                      >
                        Save to template
                      </button>
                      <span className="deal-terms-form__template-link-sep" aria-hidden="true">·</span>
                    </>
                  )}
                  <button
                    type="button"
                    className="deal-terms-form__template-link"
                    onClick={templateApi.handlers.handleSaveAsNewOpen}
                    data-testid="deal-terms-form-template-save-as-new"
                  >
                    Save as new
                  </button>
                </div>
              )}
            </div>
            <div className="deal-terms-form__template-actions">
              <V3OfferOriginCard />
            </div>
          </div>
        </div>
        )}

        {/* O-2783: in Pool mode the guarantee + upside % live on the shared pool
          * deal (configured in V3PoolDealSection), so the per-offer payout inputs
          * are hidden here. The offer template + Role stay visible. */}
        {!poolMode && (
          <div className="deal-terms-form__group">
        <label className="deal-terms-form__label deal-terms-form__field--guarantee">
          Guarantee
          <div className="deal-terms-form__currency">
            <span className="deal-terms-form__currency-prefix">$</span>
            <input
              type="number"
              step="0.01"
              className="deal-terms-form__input deal-terms-form__input--right"
              value={offer.dollar_amount ?? ''}
              onChange={e =>
                onChangeField(
                  'dollar_amount',
                  e.target.value === '' ? null : Number(e.target.value),
                )
              }
            />
            <span className="deal-terms-form__currency-suffix">{currencyLabel}</span>
          </div>
        </label>

        <div className="deal-terms-form__label deal-terms-form__field--operator">
          <span className="deal-terms-form__label-spacer" aria-hidden="true">&nbsp;</span>
          <div className="deal-terms-form__operator-group" role="radiogroup" aria-label="Guarantee combiner">
            <button
              type="button"
              className={`deal-terms-form__operator-btn ${operator === AND ? 'deal-terms-form__operator-btn--active' : ''}`}
              onClick={() => onChangeField('dollar_or_percentage_operator', AND)}
              aria-pressed={operator === AND}
              aria-label="Guarantee plus percent"
            >
              +
            </button>
            <button
              type="button"
              className={`deal-terms-form__operator-btn ${operator === OR ? 'deal-terms-form__operator-btn--active' : ''}`}
              onClick={() => onChangeField('dollar_or_percentage_operator', OR)}
              aria-pressed={operator === OR}
              aria-label="Guarantee versus percent"
            >
              vs
            </button>
          </div>
        </div>

        <label className="deal-terms-form__label deal-terms-form__field--percent">
          % of ticket sales
          <div className="deal-terms-form__percent">
            <input
              type="number"
              step="0.01"
              className="deal-terms-form__input"
              value={offer.upside_percentage ?? ''}
              onChange={e =>
                onChangeField(
                  'upside_percentage',
                  e.target.value === '' ? null : Number(e.target.value),
                )
              }
            />
            <span className="deal-terms-form__percent-suffix">%</span>
          </div>
        </label>
          </div>
        )}

        <label className="deal-terms-form__label deal-terms-form__field--role">
          Role
          <Select
            options={actTypeOptions}
            value={selectedRole}
            onChange={opt => onChangeActType(opt ? opt.value : null)}
            classNamePrefix="deal-terms-role-select"
            formatOptionLabel={formatRoleOption}
            isSearchable={false}
          />
        </label>
      </div>

      {(showBreakeven || showBreakevenForecast) && (
        <div
          className="deal-terms-form__label deal-terms-form__aside"
          data-testid="deal-terms-form-breakeven"
        >
          <span className="deal-terms-form__label-text">
            Tickets to breakeven
            {showBreakeven && (
            <Tippy
              content={(
                <TicketsToBreakevenTooltip
                  offeredDetail={breakevenOffered}
                  forecastDetail={breakevenForecast}
                />
              )}
              placement="top"
              animation="shift-away"
              theme="material"
              maxWidth={390}
            >
              <button
                type="button"
                className="deal-terms-form__breakeven-info"
                aria-label="How is this calculated?"
              >
                <i className="fas fa-info-circle" aria-hidden="true" />
              </button>
            </Tippy>
            )}
          </span>
          <div className="deal-terms-form__breakeven">
            {showBreakeven && (
              <>
                <span className="deal-terms-form__breakeven-value">
                  {breakevenCountLabel(breakevenOffered)}
                </span>
                <span className="deal-terms-form__breakeven-caption">as offered</span>
              </>
            )}
            {showBreakevenForecast && (
              <span
                className={`deal-terms-form__breakeven-forecast${showBreakeven ? '' : ' deal-terms-form__breakeven-forecast--only'}`}
              >
                {breakevenCountLabel(breakevenForecast)} forecast
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

V3OfferDealTermsForm.propTypes = {
  offer: PropTypes.shape({
    dollar_amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    upside_percentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    dollar_or_percentage_operator: PropTypes.string,
    template_id: PropTypes.number,
    template: PropTypes.shape({ name: PropTypes.string }),
    origin_modified: PropTypes.bool,
  }).isRequired,
  actType: PropTypes.string,
  onChangeField: PropTypes.func.isRequired,
  onChangeActType: PropTypes.func.isRequired,
  poolMode: PropTypes.bool,
  calculated: PropTypes.object,
};

V3OfferDealTermsForm.defaultProps = {
  actType: null,
  poolMode: false,
  calculated: null,
};

export default V3OfferDealTermsForm;
